import React, { useState, useEffect } from "react";
import Page from "components/Page";
import { ReactComponent as TimeIcon } from "assets/heroicons/icon-clock.svg";
import { ReactComponent as UserIcon } from "assets/icons/icon-user.svg";
import { ReactComponent as UsersIcon } from "assets/icons/icon-user-couple.svg";
import { ReactComponent as OfficeIcon } from "assets/icons/icon-office.svg";
import { fetchCognitoUser, disableCognitoUserMFA } from "api";
import ReactTimeAgo from "react-time-ago/tooltip";
import Button from "components/Button";
import { useWrappedUrqlMutation } from "toolbox";
import { useGetUserQuery } from "./GetUser.generated";
import { useGetOrganisationUsersQuery } from "./GetOrganisationUsers.generated";
import { useSetUserSelectedOrgMutation } from "./SetUserSelectedOrg.generated";
import { useAddOrganisationUserMutation } from "./AddOrganisationUser.generated";

import CardWithAside from "components/Card/CardWithAside";
import MediumPill from "components/MediumPill/MediumPill";
import ScrollableBox from "components/ScrollableBox/ScrollableBox";
import SearchInput from "components/SearchInput";
import Stack from "toolbox/src/components/Stack/Stack";
import NoEntitiesMessage from "components/NoEntitiesMessage/NoEntitiesMessage";

import Select from "components/Select";
import { toast } from "react-toastify";
import Card from "components/Card/Card";
import Checkbox from "components/Checkbox";
import { useRemoveOrganisationUserByIdMutation } from "./RemoveOrganisationUser.generated";

const User = ({ id = null, isCognitoAdmin = false, ...rest }) => {
  const [loading, setLoading] = useState(false);
  const [cognitoUser, setCognitoUser] = useState(null);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    if (id) {
      console.log("Fetching user info from Cognito API...");
      fetchCognitoUser(id).then((user) => setCognitoUser(user));
    }
  }, [id]);

  const [userResult] = useGetUserQuery({
    variables: { id },
  });

  const [orgUsersResult] = useGetOrganisationUsersQuery({
    variables: { user_id: id },
  });

  const updateUser = useWrappedUrqlMutation(useSetUserSelectedOrgMutation, {
    onSuccess: () => {
      toast(
        "Updated user successfully. They will need to refresh the page if already logged in."
      );
    },
    onError: (err) => {
      console.log(err);
      toast.error("Something went wrong. Could not update user.");
    },
  });

  const addUserToOrg = useWrappedUrqlMutation(useAddOrganisationUserMutation, {
    onError: (err) => {
      console.log(err);
      toast.error("Something went wrong. Could not update organisation.");
    },
  });

  const removeUserFromOrg = useWrappedUrqlMutation(
    useRemoveOrganisationUserByIdMutation,
    {
      onError: (err) => {
        console.log(err);
        toast.error("Something went wrong. Could not update organisation.");
      },
    }
  );

  if (id == null || !userResult.data || !orgUsersResult.data) {
    return null;
  }

  const app_user = userResult.data.app_users_by_pk;
  const orgs = orgUsersResult.data.organisations;

  const selectedCount = app_user.organisations.length;

  const disableMFAButtonHandler = async () => {
    setLoading(true);
    try {
      await disableCognitoUserMFA(cognitoUser?.Username);
      fetchCognitoUser(id).then((user) => setCognitoUser(user));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Page
      title={id}
      icon={<UserIcon className="inline w-10 h-10 mr-2 -mb-1 text-gray-500" />}
      {...rest}
    >
      <div className="cursor-default select-none">
        {isCognitoAdmin && (
          <span className="inline-block px-2 py-1 mr-2 text-sm font-semibold text-red-600 bg-white rounded">
            <span>ADMIN USER</span>
          </span>
        )}
        <span className="inline-block px-2 py-1 text-sm font-semibold bg-white rounded text-primary-600">
          {cognitoUser?.PreferredMfaSetting ||
          cognitoUser?.UserMFASettingList?.length > 0 ? (
            <span className="text-green-700">MFA ENABLED</span>
          ) : (
            <span className="text-gray-600">MFA DISABLED</span>
          )}
        </span>
      </div>

      <div>
        <TimeIcon className="inline w-5 h-5 mb-1 mr-1 text-gray-500" /> Last
        seen:{" "}
        {Date.parse(app_user.last_logged_in) > 0 ? (
          <ReactTimeAgo date={Date.parse(app_user.last_logged_in)} />
        ) : (
          "never"
        )}
      </div>

      <Card title="Active Organisation">
        <Select
          className="mt-2"
          label="Selected Organisation"
          value={app_user?.selected_organisation?.id}
          onChange={(event) => {
            updateUser.dispatch({
              id,
              newSelection: Number(event.target.value),
            });
          }}
        >
          {app_user?.organisations.map(({ organisation: { id, name } }, i) => (
            <option key={i} value={id}>
              {name}
            </option>
          ))}
        </Select>
      </Card>

      <CardWithAside
        title="Visible Organisations"
        aside={
          <Stack>
            <SearchInput
              placeholder="Filter"
              value={filter}
              onResetClick={() => setFilter("")}
              onChange={(event) => {
                setFilter(event.target.value);
              }}
            />
            <p className="text-sm">Organisations which this user can see.</p>
          </Stack>
        }
      >
        <ScrollableBox>
          {orgs
            ?.filter(({ name }) =>
              name?.toLowerCase().includes(filter.toLowerCase())
            )
            ?.map(({ id, name, organisation_users }) => {
              const isSelected = organisation_users.length > 0;

              const toggleOrgPermission = async () => {
                if (isSelected) {
                  if (selectedCount <= 1) {
                    console.log("warning");
                    return;
                  }

                  await removeUserFromOrg.dispatch({
                    id: organisation_users[0].id,
                  });
                  if (app_user?.selected_organisation?.id === id) {
                    await updateUser.dispatch({
                      id: app_user.id,
                      newSelection:
                        app_user?.organisations.length > 1
                          ? app_user?.organisations[0]?.organisation?.id
                          : null,
                    });
                  }
                } else {
                  await addUserToOrg.dispatch({
                    organisation_id: id,
                    user_id: app_user.id,
                  });
                  // if (app_user?.selected_organisation?.id == null)
                  updateUser.dispatch({
                    id: app_user.id,
                    newSelection: id,
                  });
                }
              };

              return (
                <MediumPill
                  title={name}
                  Icon={OfficeIcon}
                  key={id}
                  className={isSelected ? "bg-jcap-100 text-jcap-700" : ""}
                >
                  <Checkbox
                    onChange={toggleOrgPermission}
                    checked={isSelected}
                  />
                </MediumPill>
              );
            })}
          {!orgs.length && (
            <NoEntitiesMessage>No organisations found.</NoEntitiesMessage>
          )}
        </ScrollableBox>
      </CardWithAside>

      {(cognitoUser?.PreferredMfaSetting ||
        cognitoUser?.UserMFASettingList?.length > 0) && (
        <Button
          className="btn btn-warning"
          onClick={disableMFAButtonHandler}
          disabled={loading}
        >
          {loading ? "Disabling..." : "Disable MFA"}
        </Button>
      )}
    </Page>
  );
};

export const NoUser = () => (
  <Page title="">
    <div className="absolute inset-0 flex flex-col items-center justify-center">
      <UsersIcon className="w-full h-auto max-w-sm text-gray-400" />
      <h2 className="text-2xl font-semibold text-gray-400">No User Selected</h2>
    </div>
  </Page>
);

export default User;
